import React from 'react';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';
import { FileText, Scale, Shield, Clock, AlertTriangle, CheckCircle } from 'lucide-react';

export default function TermsOfBusiness() {
  return (
    <PublicLayout>
      <SEO 
        title="Terms of Business | LetWise"
        description="Terms of Business for C&C LET MANAGEMENT LIMITED trading as LetWise. Our service agreement for landlords, tenants, and property management services."
      />
      
      <div className="bg-slate-50 min-h-screen">
        {/* Header */}
        <div className="bg-primary text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <FileText className="w-16 h-16 mx-auto mb-6 opacity-80" />
              <h1 className="text-4xl font-bold mb-4">Terms of Business</h1>
              <p className="text-xl opacity-90">
                C&C LET MANAGEMENT LIMITED trading as LetWise
              </p>
              <p className="text-sm mt-4 opacity-75">
                Last updated: January 2026
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            {/* Company Details */}
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Company Information</h2>
              <div className="grid md:grid-cols-2 gap-6 text-slate-600">
                <div>
                  <p className="font-medium text-slate-900">Registered Company Name:</p>
                  <p>C&C LET MANAGEMENT LIMITED</p>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Trading As:</p>
                  <p>LetWise</p>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Company Number:</p>
                  <p>SC869470</p>
                </div>
                <div>
                  <p className="font-medium text-slate-900">PRS Membership:</p>
                  <p>PRS056298</p>
                </div>
                <div className="md:col-span-2">
                  <p className="font-medium text-slate-900">Registered Office:</p>
                  <p>Unit 4, 416 Hamilton Road, Cambuslang, Glasgow, Scotland, G72 7XR</p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <section className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Scale className="w-6 h-6 text-primary" />
                1. Introduction and Scope
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  These Terms of Business govern the relationship between C&C LET MANAGEMENT LIMITED 
                  trading as LetWise ("we", "us", "our") and all users of our property management 
                  platform and services ("you", "your").
                </p>
                <p>
                  Our services include but are not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Property listing and marketing services</li>
                  <li>Tenant finding and referencing</li>
                  <li>Tenancy management and administration</li>
                  <li>Rent collection and financial management</li>
                  <li>Maintenance coordination</li>
                  <li>Compliance management</li>
                  <li>Deposit protection administration</li>
                </ul>
              </div>
            </section>

            {/* Section 2 */}
            <section className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Shield className="w-6 h-6 text-primary" />
                2. Regulatory Compliance
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  We are a member of the Property Redress Scheme (PRS) with membership number 
                  <strong> PRS056298</strong>. This provides you with access to an independent 
                  complaints resolution service.
                </p>
                <h3 className="text-lg font-semibold text-slate-900 mt-6">2.1 England & Wales</h3>
                <p>
                  Our services comply with the Housing Act 1988, Housing Act 2004, Tenant Fees Act 2019, 
                  and the Renters' Rights Act 2025. We ensure all tenancy deposits are protected in 
                  accordance with legal requirements.
                </p>
                <h3 className="text-lg font-semibold text-slate-900 mt-6">2.2 Scotland</h3>
                <p>
                  We comply with the Private Housing (Tenancies) (Scotland) Act 2016, Housing (Scotland) 
                  Act 2006, and related regulations. Scottish deposits are protected through approved 
                  tenancy deposit schemes.
                </p>
                <h3 className="text-lg font-semibold text-slate-900 mt-6">2.3 Consumer Rights</h3>
                <p>
                  We operate in accordance with the Consumer Rights Act 2015 and Consumer Contracts 
                  (Information, Cancellation and Additional Charges) Regulations 2013.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Clock className="w-6 h-6 text-primary" />
                3. Our Services
              </h2>
              <div className="space-y-4 text-slate-600">
                <h3 className="text-lg font-semibold text-slate-900">3.1 For Landlords</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Property marketing on major portals (Rightmove, Zoopla, OnTheMarket)</li>
                  <li>Professional photography and virtual tours</li>
                  <li>Tenant referencing and background checks</li>
                  <li>Tenancy agreement preparation</li>
                  <li>Deposit registration and management</li>
                  <li>Rent collection and accounting</li>
                  <li>Property inspections and inventory</li>
                  <li>Maintenance coordination</li>
                  <li>Compliance certifications management</li>
                  <li>24/7 emergency support</li>
                </ul>
                
                <h3 className="text-lg font-semibold text-slate-900 mt-6">3.2 For Tenants</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Property search and viewing arrangements</li>
                  <li>Online application processing</li>
                  <li>Tenancy documentation</li>
                  <li>Maintenance reporting portal</li>
                  <li>Secure online payments</li>
                  <li>Document storage and access</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">4. Fees and Charges</h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  Our fees are clearly displayed on our website and will be confirmed in writing 
                  before any services commence. In accordance with the Tenant Fees Act 2019 (England), 
                  we do not charge prohibited fees to tenants.
                </p>
                <h3 className="text-lg font-semibold text-slate-900 mt-6">4.1 Permitted Tenant Payments</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Rent</li>
                  <li>Security deposit (maximum 5 weeks' rent)</li>
                  <li>Holding deposit (maximum 1 week's rent)</li>
                  <li>Payment for default (late rent, lost keys)</li>
                  <li>Payment for variation, assignment, or novation of tenancy</li>
                  <li>Payment for early termination when requested by tenant</li>
                </ul>
              </div>
            </section>

            {/* Section 5 */}
            <section className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">5. Data Protection</h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  We process personal data in accordance with the UK General Data Protection Regulation 
                  (UK GDPR) and the Data Protection Act 2018. Our Privacy Policy, available on our 
                  website, provides full details of how we collect, use, and protect your data.
                </p>
                <p>
                  We may share your information with:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Referencing agencies for tenant checks</li>
                  <li>Deposit protection schemes</li>
                  <li>Contractors for property maintenance</li>
                  <li>Legal and regulatory bodies where required</li>
                  <li>Credit reference agencies</li>
                </ul>
              </div>
            </section>

            {/* Section 6 */}
            <section className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">6. Limitation of Liability</h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  We maintain professional indemnity insurance. Our liability is limited to the 
                  extent permitted by law. We are not liable for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Actions or omissions of landlords, tenants, or third parties</li>
                  <li>Loss of rental income due to tenant default</li>
                  <li>Property damage beyond our reasonable control</li>
                  <li>Consequential or indirect losses</li>
                </ul>
              </div>
            </section>

            {/* Section 7 */}
            <section className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">7. Termination</h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  Either party may terminate this agreement by providing written notice as specified 
                  in the service agreement. Notice periods and any applicable fees will be detailed 
                  in your individual contract.
                </p>
                <p>
                  Upon termination:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>All outstanding fees become immediately payable</li>
                  <li>We will provide all relevant documentation within 14 days</li>
                  <li>Deposit transfers will be arranged as required</li>
                  <li>Access to our platform will be restricted to read-only</li>
                </ul>
              </div>
            </section>

            {/* Section 8 */}
            <section className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-primary" />
                8. Complaints
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  We are committed to providing excellent service. If you are dissatisfied, please 
                  refer to our separate <a href="/complaints" className="text-primary hover:underline">
                  Customer Complaints Procedure</a>.
                </p>
                <p>
                  If we cannot resolve your complaint, you may refer it to the Property Redress Scheme:
                </p>
                <div className="bg-slate-50 p-4 rounded-lg mt-4">
                  <p className="font-medium">Property Redress Scheme</p>
                  <p>Website: <a href="https://www.theprs.co.uk" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">www.theprs.co.uk</a></p>
                  <p>Email: info@theprs.co.uk</p>
                  <p>Phone: 0333 321 9418</p>
                </div>
              </div>
            </section>

            {/* Section 9 */}
            <section className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">9. Governing Law</h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  These Terms of Business are governed by the laws of Scotland. Any disputes shall 
                  be subject to the exclusive jurisdiction of the Scottish courts, except where 
                  alternative dispute resolution is agreed.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-primary/5 border border-primary/20 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-primary" />
                Contact Us
              </h2>
              <div className="text-slate-600 space-y-2">
                <p><strong>C&C LET MANAGEMENT LIMITED</strong> trading as <strong>LetWise</strong></p>
                <p>Unit 4, 416 Hamilton Road, Cambuslang, Glasgow, Scotland, G72 7XR</p>
                <p>Company Number: SC869470</p>
                <p>PRS Membership: PRS056298</p>
                <p className="mt-4">
                  Email: <a href="mailto:info@letwise.io" className="text-primary hover:underline">info@letwise.io</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
